import { useTranslation } from '@repo/i18n'
import {
	type ColumnDef,
	type ColumnSizingState,
	functionalUpdate,
	getCoreRowModel,
	type Header,
	type HeaderGroup,
	type OnChangeFn,
	type SortingState,
	useReactTable
} from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, CircleAlert, Inbox } from 'lucide-react'
import {
	Fragment,
	type ReactNode,
	type PointerEvent as ReactPointerEvent,
	useRef,
	useState
} from 'react'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '#components/empty'
import { Grid, GridHeader } from '#components/grid'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink
} from '#components/pagination'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '#components/select'
import { cn } from '#lib/utils'
import { DataTableBody } from './body'
import { DataTableHeaderCell, type DataTableSelectAll } from './header-cell'
import { DATA_TABLE_SURFACE_WIDTH } from './pin-styles'
import { useDataTableSelection, withSelectPinning } from './selection'
import { useDataTableSizing } from './sizing'
import {
	type DataTableColumn,
	type DataTableColumnMenuRender,
	type DataTableColumnStateConfig,
	type DataTableEmptyConfig,
	type DataTableLoadMoreConfig,
	type DataTablePaginationConfig,
	type DataTableSelectionConfig,
	type DataTableSortConfig,
	type DataTableStateConfig,
	getColumnLabel,
	makeInitialColumnState,
	SELECT_COLUMN_ID
} from './types'

const EMPTY_ROWS: never[] = []
export function useDataTableColumnState<TData>(
	columns: DataTableColumn<TData>[]
) {
	const [value, onChange] = useState(() => makeInitialColumnState(columns))
	return { value, onChange }
}

function DataTableHeader<TData>(props: {
	headerGroups: HeaderGroup<TData>[]
	selectAll: DataTableSelectAll | undefined
	enableColumnMenu: boolean | undefined
	renderColumnMenuItems: DataTableColumnMenuRender<TData> | undefined
	onResetColumn: (columnId: string) => void
	onResizeStart: (
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) => void
}) {
	return (
		<GridHeader
			className='sticky top-0 bg-muted'
			data-table-width='true'
			style={{
				width: DATA_TABLE_SURFACE_WIDTH,
				zIndex: 5
			}}
		>
			{props.headerGroups.map((headerGroup, groupIndex) => (
				// biome-ignore lint/a11y/useFocusableInteractive: DataTable uses div-based ARIA table rows for virtualization, matching the shared grid primitive.
				// biome-ignore lint/a11y/useSemanticElements: Native table rows cannot be absolutely positioned for the virtualized body.
				<div
					aria-rowindex={groupIndex + 1}
					className='flex'
					data-slot='grid-row'
					data-table-width='true'
					key={headerGroup.id}
					role='row'
					style={{
						width: DATA_TABLE_SURFACE_WIDTH,
						height: 48
					}}
				>
					{headerGroup.headers.map((header, index) => (
						<Fragment key={header.id}>
							{header.column.getIsPinned() === 'right' &&
								header.column.getIsFirstColumn('right') && (
									<div
										aria-hidden='true'
										className='min-w-0 flex-1'
									/>
								)}
							<DataTableHeaderCell
								colIndex={index + 1}
								enableColumnMenu={props.enableColumnMenu}
								header={header}
								onResetColumn={props.onResetColumn}
								onResizeStart={props.onResizeStart}
								renderColumnMenuItems={
									props.renderColumnMenuItems
								}
								selectAll={props.selectAll}
							/>
						</Fragment>
					))}
				</div>
			))}
		</GridHeader>
	)
}

function makeColumnDef<TData extends { id: string }>(
	column: DataTableColumn<TData>
): ColumnDef<TData> {
	return {
		id: column.id,
		accessorFn: (row) => row,
		header: () => column.header,
		cell: (ctx) => column.cell(ctx.row.original),
		size:
			column.flex !== undefined
				? (column.minWidth ?? 50)
				: (column.width ?? column.minWidth ?? 200),
		minSize: column.minWidth ?? 50,
		maxSize: column.maxWidth,
		enableResizing: column.enableResizing,
		enableHiding: column.hideable !== false,
		enablePinning: column.pinnable === false ? false : undefined,
		enableSorting: column.sortKey !== undefined,
		sortDescFirst: false,
		meta: {
			label: getColumnLabel(column),
			description: column.description,
			enableColumnMenu: column.enableColumnMenu,
			renderColumnMenuItems: column.renderColumnMenuItems,
			className: column.className
		}
	}
}

function getSorting<TData, TSortKey extends string>(
	columns: DataTableColumn<TData, TSortKey>[],
	sort: DataTableSortConfig<TSortKey> | undefined
) {
	const columnIdToSortKey = new Map<string, TSortKey>()
	const sortKeyToColumnId = new Map<string, string>()
	for (const column of columns) {
		if (column.sortKey !== undefined) {
			columnIdToSortKey.set(column.id, column.sortKey)
			sortKeyToColumnId.set(column.sortKey, column.id)
		}
	}

	const columnId =
		sort?.state.by === undefined
			? undefined
			: sortKeyToColumnId.get(sort.state.by)
	const state: SortingState =
		columnId === undefined
			? []
			: [
					{
						id: columnId,
						desc: sort?.state.direction === 'desc'
					}
				]
	const onChange: OnChangeFn<SortingState> | undefined = sort
		? (updater) => {
				const next = functionalUpdate(updater, state)[0]
				const by =
					next === undefined
						? undefined
						: columnIdToSortKey.get(next.id)
				sort.onSortChange(
					next === undefined || by === undefined
						? {}
						: { by, direction: next.desc ? 'desc' : 'asc' }
				)
			}
		: undefined

	return { onChange, state }
}

interface DataTableProps<
	TData extends { id: string },
	TSortKey extends string = string
> {
	columns: DataTableColumn<TData, TSortKey>[]
	data?: TData[]
	state?: DataTableStateConfig
	className?: string
	columnsState?: DataTableColumnStateConfig
	rowHeight?: number
	/**
	 * Per-row background tint (or any class); return undefined for the default
	 * `bg-card`. A tint must be opaque: pinned cells inherit the row background
	 * to cover the cells scrolling underneath.
	 */
	rowClassName?: (row: TData) => string | undefined
	/** Click anywhere on a row (except interactive cells) to act on it. */
	onRowClick?: (row: TData) => void
	overscan?: number
	loadMore?: DataTableLoadMoreConfig
	sort?: DataTableSortConfig<TSortKey>
	pagination?: DataTablePaginationConfig
	selection?: DataTableSelectionConfig<TData>
	/** Show the per-column header actions menu across the table. Default true. Override per column via `DataTableColumn.enableColumnMenu`. */
	enableColumnMenu?: boolean
	/** Default custom-items renderer for every column's actions menu. Override per column via `DataTableColumn.renderColumnMenuItems`. */
	renderColumnMenuItems?: DataTableColumnMenuRender<TData>
}

export function DataTable<
	TData extends { id: string },
	TSortKey extends string = string
>(props: DataTableProps<TData, TSortKey>) {
	const { t } = useTranslation()
	const containerRef = useRef<HTMLDivElement>(null)
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
	const loadMore = props.pagination ? undefined : props.loadMore
	const isLoading =
		props.state?.loading !== undefined && props.state.loading !== false
	const loadingContent =
		typeof props.state?.loading === 'boolean'
			? undefined
			: props.state?.loading
	const isError =
		props.state?.error !== undefined && props.state.error !== false
	const errorContent =
		typeof props.state?.error === 'boolean' ? undefined : props.state?.error

	const selection = props.selection
	const data = props.data ?? EMPTY_ROWS

	/** A leading checkbox column is injected so it joins sizing/pinning/virtualization. */
	const selectColumn: DataTableColumn<TData, TSortKey> | null = selection
		? {
				id: SELECT_COLUMN_ID,
				header: null,
				cell: () => null,
				width: 44,
				minWidth: 44,
				maxWidth: 44,
				enableResizing: false,
				hideable: false,
				pinned: 'left'
			}
		: null
	const allColumns = selectColumn
		? [selectColumn, ...props.columns]
		: props.columns

	const { headerSelectAll, bodySelection } = useDataTableSelection({
		selection,
		data,
		selectRowLabel: t('shared:data_table.select_row'),
		selectAllLabel: t('shared:data_table.select_all')
	})

	/**
	 * `accessorFn` is required for TanStack to expose sortable headers;
	 * rendering and server-side sort mapping stay explicit.
	 */
	const tableColumns: ColumnDef<TData>[] = allColumns.map(makeColumnDef)

	const defaultColumnState = makeInitialColumnState(allColumns)

	const sorting = getSorting(allColumns, props.sort)

	const table = useReactTable({
		columns: tableColumns,
		data,
		defaultColumn: { minSize: 50 },
		getCoreRowModel: getCoreRowModel(),
		getRowId: (row) => row.id,
		enableColumnResizing: true,
		enableSorting: props.sort !== undefined,
		enableMultiSort: false,
		columnResizeMode: 'onEnd',
		state: {
			...(props.columnsState
				? {
						columnVisibility: props.columnsState.value.visibility,
						columnOrder: props.columnsState.value.order,
						columnPinning: selection
							? withSelectPinning(
									props.columnsState.value.pinning
								)
							: props.columnsState.value.pinning
					}
				: {}),
			columnSizing,
			sorting: sorting.state
		},
		onColumnSizingChange: setColumnSizing,
		onSortingChange: sorting.onChange,
		manualSorting: props.sort !== undefined,
		...(props.columnsState
			? {
					onColumnVisibilityChange: (updater) => {
						props.columnsState?.onChange((prev) => ({
							...prev,
							visibility: functionalUpdate(
								updater,
								prev.visibility
							)
						}))
					},
					onColumnOrderChange: (updater) => {
						props.columnsState?.onChange((prev) => ({
							...prev,
							order: functionalUpdate(updater, prev.order)
						}))
					},
					onColumnPinningChange: (updater) => {
						props.columnsState?.onChange((prev) => ({
							...prev,
							pinning: functionalUpdate(updater, prev.pinning)
						}))
					}
				}
			: {
					initialState: {
						columnVisibility: defaultColumnState.visibility,
						columnOrder: defaultColumnState.order,
						columnPinning: defaultColumnState.pinning
					}
				})
	})

	const {
		columnSizeVars,
		columnsKey,
		leafColumns,
		onResetColumn,
		onResizeStart
	} = useDataTableSizing({
		table,
		columns: allColumns,
		containerRef
	})

	const { rows } = table.getRowModel()
	const headerGroups = table.getHeaderGroups()
	const isEmpty =
		!(isLoading || isError) &&
		rows.length === 0 &&
		loadMore?.hasMore !== true

	return (
		<div
			className={cn(
				'flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-md border bg-card',
				props.className
			)}
		>
			<div className='relative flex min-h-0 w-full min-w-0 flex-1 flex-col'>
				<Grid
					aria-busy={isLoading ? true : undefined}
					aria-colcount={leafColumns.length}
					aria-rowcount={
						isLoading
							? -1
							: headerGroups.length +
								rows.length +
								Number(loadMore?.loading === true)
					}
					className='relative isolate min-h-0 w-full min-w-0 flex-1 overflow-auto overscroll-none [overflow-anchor:none]'
					ref={containerRef}
					style={columnSizeVars}
				>
					<DataTableHeader
						enableColumnMenu={props.enableColumnMenu}
						headerGroups={headerGroups}
						onResetColumn={onResetColumn}
						onResizeStart={onResizeStart}
						renderColumnMenuItems={props.renderColumnMenuItems}
						selectAll={headerSelectAll}
					/>
					<DataTableBody
						columnsKey={columnsKey}
						headerRowCount={headerGroups.length}
						isLoading={isLoading}
						leafColumns={leafColumns}
						loaderText={t('shared:data_table.loading_more')}
						loadingRows={props.state?.loadingRows ?? 5}
						loadingState={loadingContent}
						loadMore={loadMore}
						onRowClick={props.onRowClick}
						overscan={props.overscan ?? 5}
						pageIndex={props.pagination?.pageIndex}
						rowClassName={props.rowClassName}
						rowHeight={props.rowHeight ?? 42}
						rows={rows}
						scrollRef={containerRef}
						selection={bodySelection}
					/>
				</Grid>
				<DataTableStateOverlay
					active={isError}
					content={errorContent}
					variant='error'
				/>
				<DataTableStateOverlay
					active={isEmpty}
					content={props.state?.empty}
					variant='empty'
				/>
			</div>
			{props.pagination && <DataTablePagination {...props.pagination} />}
		</div>
	)
}

function DataTableStateOverlay(
	props: { active: boolean } & (
		| { variant: 'error'; content: DataTableStateConfig['error'] }
		| { variant: 'empty'; content: DataTableStateConfig['empty'] }
	)
) {
	const { t } = useTranslation()
	/** Keep live regions mounted so state changes are announced reliably. */
	return (
		<div
			aria-live={props.variant === 'error' ? 'assertive' : 'polite'}
			className={cn(
				'absolute inset-x-0 bottom-0',
				props.active ? 'flex bg-card' : 'pointer-events-none'
			)}
			role={props.variant === 'error' ? 'alert' : 'status'}
			style={{
				top: 48,
				zIndex: props.active ? 4 : -1
			}}
		>
			{props.active &&
				(props.variant === 'error' ? (
					(props.content ?? (
						<Empty>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<CircleAlert />
								</EmptyMedia>
								<EmptyTitle>
									{t('shared:data_table.error')}
								</EmptyTitle>
							</EmptyHeader>
						</Empty>
					))
				) : (
					<DataTableEmpty
						config={
							typeof props.content === 'string'
								? { title: props.content }
								: props.content
						}
						fallbackTitle={t('shared:data_table.no_results')}
					/>
				))}
		</div>
	)
}

function DataTableEmpty(props: {
	config?: DataTableEmptyConfig
	fallbackTitle: ReactNode
}) {
	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					{props.config?.icon ?? <Inbox />}
				</EmptyMedia>
				<EmptyTitle>
					{props.config?.title ?? props.fallbackTitle}
				</EmptyTitle>
				{props.config?.description && (
					<EmptyDescription>
						{props.config.description}
					</EmptyDescription>
				)}
			</EmptyHeader>
			{props.config?.action && (
				<EmptyContent>{props.config.action}</EmptyContent>
			)}
		</Empty>
	)
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end'

function buildPageRange(current: number, total: number): PageItem[] {
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1)
	}
	const curr = current + 1
	if (curr <= 3) {
		return [1, 2, 3, 4, 5, 'ellipsis-end', total]
	}
	if (curr >= total - 2) {
		return [
			1,
			'ellipsis-start',
			total - 4,
			total - 3,
			total - 2,
			total - 1,
			total
		]
	}
	return [
		1,
		'ellipsis-start',
		curr - 1,
		curr,
		curr + 1,
		'ellipsis-end',
		total
	]
}

function DataTablePagination(props: DataTablePaginationConfig) {
	const { t } = useTranslation()
	const totalPages = Math.max(1, props.pageCount)
	const current = Math.min(Math.max(0, props.pageIndex), totalPages - 1)
	const pageSizeOptions = props.pageSizeOptions ?? [10, 20, 50, 100]
	const selectablePageSizes =
		props.pageSize !== undefined &&
		!pageSizeOptions.includes(props.pageSize)
			? [...pageSizeOptions, props.pageSize].toSorted((a, b) => a - b)
			: pageSizeOptions

	function handleNavigate(pageIndex: number) {
		if (
			pageIndex === current ||
			pageIndex < 0 ||
			pageIndex > totalPages - 1
		) {
			return
		}
		props.onPageChange(pageIndex)
	}

	return (
		<div
			className='flex shrink-0 items-center justify-between border-t bg-card'
			style={{ height: 42 }}
		>
			{props.pageSize !== undefined &&
				props.onPageSizeChange !== undefined && (
					<div className='flex items-center gap-2 px-4'>
						<span className='text-muted-foreground text-sm'>
							{t('shared:data_table.pagination.rows_per_page')}
						</span>
						<Select
							onValueChange={(value) =>
								props.onPageSizeChange?.(Number(value))
							}
							value={String(props.pageSize)}
						>
							<SelectTrigger
								className='h-7! w-[64px] py-1 text-xs [&_svg]:size-3.5'
								size='sm'
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{selectablePageSizes.map((size) => (
									<SelectItem key={size} value={String(size)}>
										{size}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
			<Pagination className='mx-0 w-fit px-4'>
				<PaginationContent>
					<PaginationItem>
						<PaginationLink
							aria-label={t(
								'shared:data_table.pagination.previous'
							)}
							disabled={current === 0}
							onClick={() => handleNavigate(current - 1)}
							size='icon-xs'
						>
							<ChevronLeft />
						</PaginationLink>
					</PaginationItem>
					{buildPageRange(current, totalPages).map((item) => {
						if (
							item === 'ellipsis-start' ||
							item === 'ellipsis-end'
						) {
							return (
								<PaginationItem key={item}>
									<PaginationEllipsis>
										<span className='sr-only'>
											{t(
												'shared:data_table.pagination.more_pages'
											)}
										</span>
									</PaginationEllipsis>
								</PaginationItem>
							)
						}
						return (
							<PaginationItem key={item}>
								<PaginationLink
									aria-label={t(
										'shared:data_table.pagination.go_to_page',
										{ page: item }
									)}
									isActive={item - 1 === current}
									onClick={() => handleNavigate(item - 1)}
									size='icon-xs'
								>
									{item}
								</PaginationLink>
							</PaginationItem>
						)
					})}
					<PaginationItem>
						<PaginationLink
							aria-label={t('shared:data_table.pagination.next')}
							disabled={current >= totalPages - 1}
							onClick={() => handleNavigate(current + 1)}
							size='icon-xs'
						>
							<ChevronRight />
						</PaginationLink>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
		</div>
	)
}
